import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * Ginny Litscher, 12 Sept 2026: the bar rotates one line at a time (Matt's brief), so it meets
 * WCAG 2.2.2 (pause, stop, hide) here, not in each block:
 * - focus inside the bar holds the line still, as hover already did;
 * - a pause button stops it until pressed again (its label changes; no aria-pressed, the same
 *   pattern as the film's control);
 * - with reduced motion set, it never starts;
 * - a hidden tab only suspends it. Horizon paused it there, and resume() refuses a paused bar,
 *   so after one tab switch it never moved again.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange);

    if (this.#reducedMotion.matches) {
      this.paused = true;
      this.#syncPauseButton();
      return;
    }

    this.play();
    this.#syncPauseButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.removeEventListener('change', this.#handleMotionChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.suspend();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.matches(':focus-within') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback until play() or the pause button restarts it.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  /**
   * The pause button: stops the rotation, or starts it again.
   */
  togglePause() {
    if (this.paused) {
      this.play();
    } else {
      this.pause();
    }
    this.#syncPauseButton();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback for a moment (hover, focus, a hidden tab).
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback, unless it was paused on purpose.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #syncPauseButton() {
    const button = this.refs.pauseButton;
    if (!button) return;

    const label = this.paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
    button.dataset.state = this.paused ? 'paused' : 'playing';
  }

  /**
   * Resume only when focus has left the bar altogether.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.resume();
  };

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #handleMotionChange = () => {
    if (this.#reducedMotion.matches) this.pause();
    this.#syncPauseButton();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
